import React, { useCallback, useReducer, useContext, useEffect } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IUpdateRestaurantPropertiesAsPatchForModGroup } from 'types';
import { SaveAsPatchActionTypes } from './saveAsPatchModalActions';
import { saveAsPatchReducer } from './saveAsPatchModalReducer';
import { saveAsPatchInitialState, SaveAsPatchContext, ISaveAsPatchState } from './saveAsPatchModalContext';
import {
	IEditModGroupRestaurantPropertiesContext,
	EditModGroupRestaurantPropertiesContext,
} from '../../../../editModGroupRestaurantPropertiesState/editModGroupRestaurantPropertiesContext';
import { convertUndefinedFields } from 'utils/helpers';
import { updateModGroupRestaurantPropertiesAsPatch } from 'backend/modifierGroups';

export const SaveAsPatchState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(saveAsPatchReducer, saveAsPatchInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		refetchRestPropsData,
		changeTypeOfModalWindow: changeTypeOfParentModalWindow,
		modGroupId,
		customerName,
		fetchedRestaurants,
		IDSelectedRestaurant,
		forceDisable,
		parents,
	} = useContext<IEditModGroupRestaurantPropertiesContext>(EditModGroupRestaurantPropertiesContext);

	const inclusive = parents.length > 0 ? parents[0].inclusive : null;

	useEffect(() => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_INITIAL_FORM_DATA,
			payload: {
				initialFormData: {
					modGroupId,
					customerName,
					inclusive,
					fetchedRestaurants,
					IDSelectedRestaurantOnRestPropsPage: IDSelectedRestaurant,
					forceDisable,
				},
			},
		});
	}, [forceDisable]);

	const saveData = useCallback(
		async (value: 'saveAndMakeOtherChanges' | 'saveAndClose') => {
			try {
				const dataForSubmit: IUpdateRestaurantPropertiesAsPatchForModGroup = {
					modifierGroupId: modGroupId,
					restaurantIds:
						state.typeOfChanges === 'basedChanges'
							? [
									state.initialFormData.IDSelectedRestaurantOnRestPropsPage || '',
									...state.IDsSelectedRestaurant,
							  ]
							: state.IDsSelectedRestaurant,
					update: {
						forceDisable: {
							shouldUpdate: state.selectedProperties.includes('forceDisable'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.forceDisable
									: state.forceDisable,
						},
						parents: {
							shouldUpdate: false,
							value: [],
						},
						children: {
							shouldUpdate: false,
							value: [],
						},
					},
				};
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateModGroupRestaurantPropertiesAsPatch(convertedData);
				if (value === 'saveAndClose') {
					changeTypeOfParentModalWindow(null);
					refetchRestPropsData(true);
				}
				if (value === 'saveAndMakeOtherChanges') {
					refetchRestPropsData(true);
					dispatch({ type: SaveAsPatchActionTypes.RESET_FIELDS });
				}
			} catch (err) {
				dispatch({
					type: SaveAsPatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'error',
					},
				});
			} finally {
				hideLoader();
			}
		},
		[showLoader, hideLoader, state],
	);

	const setTypeOfChanges = useCallback(
		(value: ISaveAsPatchState['typeOfChanges']) => {
			dispatch({
				type: SaveAsPatchActionTypes.SET_TYPE_OF_CHANGES,
				payload: {
					typeOfChanges: value,
					IDsSelectedRestaurant:
						value === 'basedChanges'
							? state.IDsSelectedRestaurant.filter(
									(id) => id !== state.initialFormData.IDSelectedRestaurantOnRestPropsPage,
							  )
							: state.IDsSelectedRestaurant,
				},
			});
		},
		[state.IDsSelectedRestaurant, state.initialFormData.IDSelectedRestaurantOnRestPropsPage],
	);

	const setIDsRestaurant = useCallback((value: ISaveAsPatchState['IDsSelectedRestaurant']) => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const setProperties = useCallback((value: ISaveAsPatchState['selectedProperties']) => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_PROPERTIES,
			payload: {
				selectedProperties: value,
			},
		});
	}, []);

	const toggleForceDisable = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_FORCE_DISABLE,
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: ISaveAsPatchState['typeOfModalWindow']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<SaveAsPatchContext.Provider
			value={{
				saveData,
				setTypeOfChanges,
				setIDsRestaurant,
				setProperties,
				toggleForceDisable,
				changeTypeOfModalWindow,
				initialFormData: state.initialFormData,
				typeOfChanges: state.typeOfChanges,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				selectedProperties: state.selectedProperties,
				forceDisable: state.forceDisable,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</SaveAsPatchContext.Provider>
	);
};
